import type { DesignSide, Gender } from "../types";
import { listWearableLabels } from "../config/wearables";

interface Props {
  gender: Gender;
  onGender: (g: Gender) => void;
  wearableLabel: string;
  selectedSide: DesignSide;
  onSelectedSide: (s: DesignSide) => void;
  onReset: () => void;
  onDownloadFrontTemplate: () => void;
  onDownloadBackTemplate: () => void;
  onDownloadPNG: () => void;
  onExportGLB: () => void;
  onSnapView: (side: "front" | "back" | "left" | "right") => void;
  debugOverlay: boolean;
  onDebugOverlay: (v: boolean) => void;
  exportBusy?: boolean;
  exportDisabled?: boolean;
}

export function LeftPanel(p: Props) {
  const labels = listWearableLabels();

  return (
    <aside className="panel left">
      <h2 className="panel-title">Setup</h2>

      <section className="group">
        <h3 className="group-title">Gender</h3>
        <div className="seg">
          {(["male", "female"] as Gender[]).map((g) => (
            <button
              key={g}
              className={`seg-btn ${p.gender === g ? "active" : ""}`}
              onClick={() => p.onGender(g)}
              type="button"
            >
              {g}
            </button>
          ))}
        </div>
      </section>

      <section className="group">
        <h3 className="group-title">Wearable</h3>
        <select
          className="input"
          value={p.wearableLabel}
          disabled
          onChange={() => { /* only one option in V1 */ }}
        >
          {labels.map((l) => (
            <option key={l.id} value={l.label}>
              {l.label}
            </option>
          ))}
        </select>
      </section>

      <section className="group">
        <h3 className="group-title">Editing Side</h3>
        <div className="seg">
          {(["front", "back"] as DesignSide[]).map((s) => (
            <button
              key={s}
              className={`seg-btn ${p.selectedSide === s ? "active" : ""}`}
              onClick={() => {
                p.onSelectedSide(s);
                // Snap the camera to match the side the user is editing
                // so they always see what they're placing.
                p.onSnapView(s);
              }}
              type="button"
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      <section className="group">
        <h3 className="group-title">Camera View</h3>
        <div className="seg">
          {(["front", "back", "left", "right"] as const).map((s) => (
            <button
              key={s}
              className="seg-btn"
              onClick={() => p.onSnapView(s)}
              type="button"
              title={`Snap camera to ${s} view`}
            >
              {s}
            </button>
          ))}
        </div>
        <label className="row" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={p.debugOverlay}
            onChange={(e) => p.onDebugOverlay(e.target.checked)}
          />
          <span className="label" style={{ marginLeft: 8 }}>
            Show printable region on model
          </span>
        </label>
      </section>

      <section className="group">
        <h3 className="group-title">Actions</h3>
        <div className="stack">
          <button className="btn" type="button" onClick={p.onReset}>
            Reset Design
          </button>
          <button
            className="btn"
            type="button"
            onClick={p.onDownloadFrontTemplate}
          >
            Download Front Template
          </button>
          <button
            className="btn"
            type="button"
            onClick={p.onDownloadBackTemplate}
          >
            Download Back Template
          </button>
          <button className="btn primary" type="button" onClick={p.onDownloadPNG}>
            Download Final PNG
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={p.onExportGLB}
            disabled={p.exportBusy || p.exportDisabled}
            title={p.exportDisabled ? "Model not ready" : undefined}
          >
            {p.exportBusy ? "Exporting…" : "Export GLB"}
          </button>
        </div>
      </section>
    </aside>
  );
}
